import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Core } from '@everfi/reactifi';
import { DesignSystem } from '@everfi/design-system';

import { getHomeRoomOldUrl } from '../common/HomeRoomUtils';
import environment from '../environment';
import { UserData } from './useUserData';

const { HOMEROOM_PLATFORM_URL, HOMEROOM_ADMIN_URL } = environment;

interface UseSidebarViewOptionsParams {
  isHomeroom: boolean;
  userData: UserData | undefined;
}

/**
 * Homeroom-only: manages the "New View / Classic View" switcher shown in the sidebar,
 * and the confirmation modal for navigating to the classic view.
 */
export const useSidebarViewOptions = ({ isHomeroom, userData }: UseSidebarViewOptionsParams) => {
  const { t } = useTranslation('layout');
  const [showModalNavigate, setShowModalNavigate] = useState(false);

  const onConfirmNavigation = async () => {
    const baseUrl = userData?.impersonator_id ? HOMEROOM_ADMIN_URL : HOMEROOM_PLATFORM_URL;
    const oldUrl = getHomeRoomOldUrl(window.location.pathname);
    const fullUrl = new URL(`${baseUrl}/${oldUrl}`);
    fullUrl.searchParams.set('try_classic_experience', 'true');
    window.location.href = fullUrl.toString();
  };

  const canUseHomeroomClassic = isHomeroom && userData?.flippers?.redirect_teacher_to_dashifi_permanently === false;
  if (!canUseHomeroomClassic) {
    return { viewOptions: null, modal: null };
  }

  const viewOptions = [
    {
      text: (
        <Core.Layout variant="flex">
          <Core.Layout>
            <Core.Layout>
              <Core.Typography fontWeight={DesignSystem.FontWeights.BOLD} margin="0">
                {t('New View')}
              </Core.Typography>
            </Core.Layout>
            <Core.Layout>{t('We\u2019re trying something new out!')}</Core.Layout>
          </Core.Layout>
          <Core.Layout ml={DesignSystem.Space.SM}>
            <Core.Icon iconName="check" />
          </Core.Layout>
        </Core.Layout>
      ),
      ariaLabel: `${t('Selected option:')} ${t('New View')} ${t('We\u2019re trying something new out!')}`,
    },
    {
      text: (
        <Core.Layout>
          <Core.Layout>
            <Core.Typography fontWeight={DesignSystem.FontWeights.BOLD} margin="0">
              {t('Classic View')}
            </Core.Typography>
          </Core.Layout>
          <Core.Layout>{t('Back to what you know')}</Core.Layout>
        </Core.Layout>
      ),
      onClick: () => setShowModalNavigate(true),
      id: 'switcher-value-option-1',
    },
  ];

  const modal = (
    <Core.Modal
      actionLabel={t('Let’s Go')}
      actionOnClick={onConfirmNavigation}
      children={t('Click “Let’s Go” to navigate back to the classic Teacher Dashboard!')}
      closeLabel={t('Cancel')}
      header={t('Taking you to a new place!')}
      isOpen={showModalNavigate}
      modalType="alert"
      onClose={() => setShowModalNavigate(false)}
    />
  );

  return { viewOptions, modal };
};
